import React, { useEffect, useRef, useState } from 'react';
import StandardDropdown from './StandardDropdown.jsx';
import DatePicker from '../DatePicker.jsx';
import {
  OVERRIDE_TYPE_OPTIONS,
  complianceTypeLabel,
  findProjectMember,
  formatResolutionFileSize,
  getIssueComplianceType,
  getPrimaryRequirementRef,
  normalizeIssueStatus,
  memberAvatarColor,
  memberInitials,
} from './complianceIssuePresenters';
import {
  PanelAbsentDocs,
  PanelEvidenceBox,
  PanelZoneField,
  PanelZoneSection,
  PanelMetaItem,
  PanelRequirementBox,
  PanelRequirementContext,
  PanelTextLink,
  PanelVerdictRow,
} from './panelSidebarPrimitives.jsx';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'review', label: 'Review' },
];

const EVIDENCE_ACCEPT = '.pdf,.dwg,.png,.jpg,.jpeg,.docx,.xlsx';

const uploadIcon = (
  <svg className="upload-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path
      d="M11 14V4M11 4L7.5 7.5M11 4L14.5 7.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 16v1a2 2 0 002 2h12a2 2 0 002-2v-1"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const errorIcon = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="5" stroke="#dc2626" strokeWidth="1.2" />
    <path d="M6 3.5v3M6 8v.5" stroke="#dc2626" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

function FieldErrorMsg({ visible, children }) {
  return (
    <div className={`field-error-msg${visible ? ' visible' : ''}`} role="alert">
      {errorIcon}
      {children}
    </div>
  );
}

function DrawingReviewLink({ onViewOnDrawing }) {
  if (!onViewOnDrawing) return null;
  return (
    <button
      type="button"
      className="panel-sidebar__scan-link"
      style={{ marginTop: 12, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
      onClick={onViewOnDrawing}
    >
      View on drawing →
    </button>
  );
}

function ComplianceCheckSections({
  issue,
  onOpenRequirementRef,
  onOpenEvidence,
  onViewOnDrawing,
}) {
  const compType = complianceTypeLabel(getIssueComplianceType(issue));
  const requirementRef = getPrimaryRequirementRef(issue);

  return (
    <>
      <PanelZoneSection title="Requirement">
        <PanelRequirementContext
          complianceTypeLabel={compType}
          requirementRef={requirementRef}
          onRefClick={(ref) => onOpenRequirementRef(ref, issue)}
        />
        {issue.expectedCompliance ? (
          <PanelRequirementBox>{issue.expectedCompliance}</PanelRequirementBox>
        ) : null}
      </PanelZoneSection>

      <PanelZoneSection
        title="Verdict"
        accessory={<PanelVerdictRow verdict={issue.verdict} inline />}
      >
        {issue.researchFinding ? (
          <PanelRequirementBox>{issue.researchFinding}</PanelRequirementBox>
        ) : null}
        <PanelEvidenceBox
          source={{
            docName: issue.evidenceDoc,
            location: issue.evidenceLocation,
            statement: issue.evidenceStatement,
          }}
          onOpen={onOpenEvidence}
        />
        <DrawingReviewLink onViewOnDrawing={onViewOnDrawing} />
      </PanelZoneSection>
    </>
  );
}

function ConflictSections({ issue, onOpenRequirementRef, onOpenSource, onViewOnDrawing }) {
  const compType = complianceTypeLabel(getIssueComplianceType(issue));
  const requirementRef = getPrimaryRequirementRef(issue);

  return (
    <>
      <PanelZoneSection title="Requirement">
        <PanelRequirementContext
          complianceTypeLabel={compType}
          requirementRef={requirementRef}
          onRefClick={(ref) => onOpenRequirementRef(ref, issue)}
        />
        {issue.conflictMode === 'rvr' && issue.expectedCompliance ? (
          <PanelRequirementBox>{issue.expectedCompliance}</PanelRequirementBox>
        ) : null}
      </PanelZoneSection>

      <PanelZoneSection title="Conflicting documents">
        <div className="conflict-source-label">Source A</div>
        <PanelEvidenceBox source={issue.sourceA} onOpen={() => onOpenSource(issue.sourceA)} />
        <div className="conflict-source-label">Source B</div>
        <PanelEvidenceBox source={issue.sourceB} onOpen={() => onOpenSource(issue.sourceB)} />
        <DrawingReviewLink onViewOnDrawing={onViewOnDrawing} />
      </PanelZoneSection>
    </>
  );
}

function GapSections({ issue, onOpenRequirementRef, onOpenSource, onViewOnDrawing }) {
  const compType = complianceTypeLabel(getIssueComplianceType(issue));
  const requirementRef = getPrimaryRequirementRef(issue);

  return (
    <>
      <PanelZoneSection title="Requirement">
        <PanelRequirementContext
          complianceTypeLabel={compType}
          requirementRef={requirementRef}
          onRefClick={(ref) => onOpenRequirementRef(ref, issue)}
        />
      </PanelZoneSection>

      <PanelZoneSection title="Verdict">
        {issue.presentIn ? (
          <>
            <div className="gap-section-label">Present in</div>
            <PanelEvidenceBox source={issue.presentIn} onOpen={() => onOpenSource(issue.presentIn)} />
          </>
        ) : null}
        {(issue.absentFrom || []).length > 0 ? (
          <>
            <div className="gap-section-label">Missing from</div>
            <PanelAbsentDocs items={issue.absentFrom} />
          </>
        ) : null}
        <DrawingReviewLink onViewOnDrawing={onViewOnDrawing} />
      </PanelZoneSection>
    </>
  );
}

function IssueAnalysisSections({
  issue,
  onOpenRequirementRef,
  onOpenEvidence,
  onOpenSource,
  onViewOnDrawing,
}) {
  const pathType = issue.pathType || issue.path_type;

  if (pathType === 'compliance_check') {
    return (
      <ComplianceCheckSections
        issue={issue}
        onOpenRequirementRef={onOpenRequirementRef}
        onOpenEvidence={onOpenEvidence}
        onViewOnDrawing={onViewOnDrawing}
      />
    );
  }
  if (pathType === 'conflict') {
    return (
      <ConflictSections
        issue={issue}
        onOpenRequirementRef={onOpenRequirementRef}
        onOpenSource={onOpenSource}
        onViewOnDrawing={onViewOnDrawing}
      />
    );
  }
  if (pathType === 'gap') {
    return (
      <GapSections
        issue={issue}
        onOpenRequirementRef={onOpenRequirementRef}
        onOpenSource={onOpenSource}
        onViewOnDrawing={onViewOnDrawing}
      />
    );
  }

  const compType = complianceTypeLabel(getIssueComplianceType(issue));
  const requirementRef = getPrimaryRequirementRef(issue);
  return (
    <PanelZoneSection title="Requirement">
      <PanelRequirementContext
        complianceTypeLabel={compType}
        requirementRef={requirementRef}
        onRefClick={(ref) => onOpenRequirementRef(ref, issue)}
      />
    </PanelZoneSection>
  );
}

function Zone3Resolution({
  issue,
  onOverrideTypeChange,
  onDismissalReasonChange,
  onResolutionAttachmentsChange,
}) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const overrideDropdownRef = useRef(null);
  const prevStatusKeyRef = useRef(normalizeIssueStatus(issue.status));
  const overrideType = issue.overrideType || '';
  const dismissalReason = issue.dismissalReason || '';
  const attachments = issue.resolutionAttachments || [];
  const statusKey = normalizeIssueStatus(issue.status);
  const isDismissed = statusKey === 'dismissed';
  const isResolved = statusKey === 'resolved';
  const overrideFieldId = `override-type-row-${issue.id}`;
  const overrideMissing = isDismissed && !overrideType;

  useEffect(() => {
    setDragOver(false);
  }, [issue.id]);

  // Auto-scroll to + focus the override dropdown the moment status becomes
  // "dismissed" — don't pre-select a value, just bring it into view. Status
  // changes go through immediately (not gated on this field being filled
  // first), since the dropdown only renders once isDismissed is already true.
  useEffect(() => {
    const prevKey = prevStatusKeyRef.current;
    prevStatusKeyRef.current = statusKey;
    if (statusKey !== 'dismissed' || prevKey === 'dismissed') return undefined;
    const el = document.getElementById(overrideFieldId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => overrideDropdownRef.current?.focus(), 300);
    return () => clearTimeout(t);
  }, [statusKey, overrideFieldId]);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const next = [
      ...attachments,
      ...incoming.map((file, idx) => ({
        id: `${Date.now()}-${idx}-${file.name}`,
        name: file.name,
        size: file.size,
        file,
      })),
    ];
    onResolutionAttachmentsChange(next);
  }

  function removeAttachment(id) {
    onResolutionAttachmentsChange(attachments.filter((a) => a.id !== id));
  }

  return (
    <>
      {isDismissed ? (
        <PanelZoneField label="Override reason" required id={overrideFieldId}>
          <StandardDropdown
            ref={overrideDropdownRef}
            variant="override-type"
            value={overrideType}
            placeholder="Select override type…"
            options={OVERRIDE_TYPE_OPTIONS}
            error={overrideMissing}
            onChange={(v) => onOverrideTypeChange(v)}
          />
          <FieldErrorMsg visible={overrideMissing}>
            Required before dismissing this issue.
          </FieldErrorMsg>
        </PanelZoneField>
      ) : null}

      {isResolved ? (
        <p className="resolution-resolved-note">
          Marking this resolved records that the team fixed it — verification status
          won't update until a new compliance check scan confirms it.
        </p>
      ) : null}

      <PanelZoneField label="Notes">
        <textarea
          id={`dismissal-notes-${issue.id}`}
          className="dismissal-textarea"
          placeholder="Add any notes about this override…"
          value={dismissalReason}
          onChange={(e) => onDismissalReasonChange(e.target.value)}
        />
      </PanelZoneField>

      <PanelZoneField label="Resolution evidence" id={`evidence-label-${issue.id}`}>
        <div
          id={`upload-area-${issue.id}`}
          className={['upload-area', dragOver ? 'drag-over' : ''].filter(Boolean).join(' ')}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
          onClick={(e) => {
            if (e.target === fileInputRef.current) return;
            fileInputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={EVIDENCE_ACCEPT}
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <div className="upload-area-inner">
            {uploadIcon}
            <span className="upload-label">
              <strong>Click to upload</strong> or drag and drop
            </span>
            <span className="upload-hint">PDF, DWG, PNG, JPG, DOCX, XLSX</span>
          </div>
        </div>
        {attachments.length > 0 ? (
          <div className="attached-files">
            {attachments.map((att) => (
              <div key={att.id} className="attached-file">
                <span className="attached-file-name" title={att.name}>
                  {att.name}
                </span>
                <span className="attached-file-size">{formatResolutionFileSize(att.size)}</span>
                <button
                  type="button"
                  className="attached-file-remove"
                  title="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAttachment(att.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </PanelZoneField>
    </>
  );
}

export default function ComplianceDetailPanel({
  issue,
  onClose,
  projectMembers = [],
  onStatusChange,
  onAssigneeChange,
  onDeadlineChange,
  onOpenRequirementRef,
  onOpenEvidence,
  onOpenSource,
  onViewOnDrawing,
  onOverrideTypeChange,
  onDismissalReasonChange,
  onResolutionAttachmentsChange,
  onFilterByScan,
}) {
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);

  useEffect(() => {
    if (!assigneeDropdownOpen) return;
    const handler = () => setAssigneeDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [assigneeDropdownOpen]);

  if (!issue) return null;

  const member = findProjectMember(projectMembers, issue.assignedTo);

  return (
    <div className="detail-panel-inner panel-sidebar">
      <header className="panel-sidebar__header">
        <div className="panel-sidebar__header-top">
          <span className="panel-sidebar__id">{issue.ncCode || issue.display_id}</span>
          <button type="button" className="panel-sidebar__close" onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>
        <h2 className="panel-sidebar__title">{issue.topic || issue.identifiedIssue}</h2>

        <div className="panel-sidebar__meta">
          <PanelMetaItem label="Status">
            <StandardDropdown
              className="panel-status-dropdown"
              variant="panel-status"
              value={normalizeIssueStatus(issue.status)}
              options={STATUS_OPTIONS}
              onChange={onStatusChange}
            />
          </PanelMetaItem>

          <PanelMetaItem label="Assigned to" fullWidth>
            <div style={{ position: 'relative', width: '100%' }}>
              <span
                className="assignee-chip-wrapper"
                onClick={(e) => { e.stopPropagation(); setAssigneeDropdownOpen(o => !o); }}
                style={{ cursor: 'pointer' }}
              >
                {member ? (
                  <>
                    <span className="assignee-chip assignee-chip--panel">
                      <span className="avatar-circle" style={{ background: memberAvatarColor(member.userId) }}>
                        {memberInitials(member.name)}
                      </span>
                      <span className="assignee-chip-name">{member.name}</span>
                      <button
                        type="button"
                        className="assignee-chip-remove"
                        title="Remove assignee"
                        onClick={(e) => { e.stopPropagation(); onAssigneeChange(''); setAssigneeDropdownOpen(false); }}
                      >
                        ×
                      </button>
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Unassigned</span>
                )}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: '#9ca3af', flexShrink: 0 }}>
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {assigneeDropdownOpen && (
                <div className="assignee-chip-menu" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="assignee-chip-menu__item"
                      onClick={() => { onAssigneeChange(''); setAssigneeDropdownOpen(false); }}
                    >
                    Unassigned
                  </div>
                  {projectMembers.map((m) => (
                    <div
                      key={m.userId}
                      className="assignee-chip-menu__item"
                      onClick={() => { onAssigneeChange(m.userId); setAssigneeDropdownOpen(false); }}
                    >
                      <span className="avatar-circle" style={{ background: memberAvatarColor(m.userId), width: 18, height: 18, fontSize: '0.55rem' }}>
                        {memberInitials(m.name)}
                      </span>
                      {m.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PanelMetaItem>

          <PanelMetaItem label="Deadline">
            <DatePicker
              value={issue.deadline || ''}
              onChange={onDeadlineChange}
              placeholder="No deadline"
            />
          </PanelMetaItem>
        </div>
      </header>

      <IssueAnalysisSections
        issue={issue}
        onOpenRequirementRef={onOpenRequirementRef}
        onOpenEvidence={onOpenEvidence}
        onOpenSource={onOpenSource}
        onViewOnDrawing={onViewOnDrawing}
      />

      <PanelZoneSection title="Resolution" zone="3" className="panel-sidebar__section--last">
        <Zone3Resolution
          issue={issue}
          onOverrideTypeChange={onOverrideTypeChange}
          onDismissalReasonChange={onDismissalReasonChange}
          onResolutionAttachmentsChange={onResolutionAttachmentsChange}
        />
      </PanelZoneSection>

      {issue.scan_id ? (
        <footer className="panel-sidebar__footer">
          <div className="panel-sidebar__context">
            <PanelTextLink
              className="panel-sidebar__scan-link"
              onClick={onFilterByScan ? () => onFilterByScan(issue.scan_id) : undefined}
              title="Filter the issues list to this scan"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
                <path d="M6 4v2l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
              Originating scan · {issue.formattedCreatedAt || issue.scan_id}
            </PanelTextLink>
          </div>
        </footer>
      ) : null}
    </div>
  );
}
